import * as React from 'react';
import { getIn } from 'formik';
import { sortBy, isEmpty, uniq } from 'lodash';
import {
  IWidgetCascadeField,
  IWidgetCascadeCombination,
  IWidgetCascadeConfig
} from '../../input-cascade-select/fields/WidgetCascadeField';
import { IWidgetCascadeRequestField } from '../../input-cascade-select/FormWidgetInputCascadeSelectView';

/**
 *  Controllo se le colonne della combinazione sono uguali alle colonne della richiesta:
 *  se sono uguali non li faccio vedere
 */
function checkColumnsValueEqual(
  configFields: IWidgetCascadeField[],
  allValues: any,
  i: number,
  comb: IWidgetCascadeCombination
) {
  let isEqual = true;
  for (const field of configFields) {
    if (!allValues[i].values) return false;
    isEqual = isEqual && comb.values[field.index] === allValues[i].values[field.index]; // prettier-ignore
  }
  return isEqual;
}

/**
 * Gestione della mutua esclusione delle colonne
 */
export function mutualExclusion(
  combinations: IWidgetCascadeCombination[],
  request: any,
  metadataId: string,
  configFields: IWidgetCascadeField[],
  index: number
) {
  // Estrapolo i valori già presenti nella richiesta
  const allValues = getIn(request, `values.${metadataId}`);

  // Se la richiesta non ha valori, ritorno le combinazioni
  if (!allValues || !allValues[0].values) return combinations;

  // Se la richiesta ha valori, controllo se la combinazione è uguale a quella già presente nella richiesta
  const availableCombinations = combinations.filter(comb => {
    for (let i = 0; i < allValues.length; i++) {
      // non filtro la combinazione corrente
      if (i === index) continue;

      // Se la combinazione è uguale a quella già presente nella richiesta, non la mostro
      if (checkColumnsValueEqual(configFields, allValues, i, comb)) return false; // prettier-ignore
    }
    return true;
  });
  return availableCombinations;
}

/**
 * Logica per gestire la selezione a preferenza multipla. Si occupa di:
 *  - Calcolare la visibilità dei campi da mostrare e il loro stato (abilitato)
 *  - Reimpostare i campi in seguito alla modifica di una voce precedente
 *  - Gestire le opzioni disponibili alla voce successiva in base alle opzioni precedenti (mutua esclusione)
 */
export function useMultiPreferenceSelect(
  config: IWidgetCascadeConfig | undefined,
  metadataId: string,
  index: number,
  fieldPrefix: string,
  request: any
) {
  const configFields = sortBy(config?.fields ?? [], 'order');

  if (!config) return [];

  // I valori selezionati nel form servono per pre-filtrare le opzioni dei
  // passi successivi
  const selectedValues = configFields
    .map(field => ({
      field,
      value: getIn(request, `${fieldPrefix}.${field.index}`) as string
    }))
    .filter(v => v.value != null);

  // Se non ho selezionato nessun valore, ritorno tutte le combinazioni altrimenti filtro
  return configFields.reduce((fields, field, i, arr) => {
    let availableCombinations =
      i === 0
        ? mutualExclusion(
            config.combinations,
            request,
            metadataId,
            configFields,
            index
          )
        : fields[i - 1].availableCombinations.filter(c => {
            return fields.every(prevField => {
              const prevFieldSelectedValue = selectedValues.find(
                s => s.field.index === prevField.field.index
              );
              return (
                !prevFieldSelectedValue ||
                prevFieldSelectedValue.value === c.values[prevField.field.index]
              );
            });
          });

    fields.push({
      field,
      value: getIn(request, `${fieldPrefix}.${field.index}`) as string,
      // Disabilitiamo sempre i campi successivi a quello da configurare per
      // garantire che si possa arrivare a una combinazione valida
      disabled: i === 0 ? false : isEmpty(fields[i - 1].value),
      // Le portiamo appresso solo per essere uste con `fields[i - 1]`
      availableCombinations,
      options: uniq(availableCombinations.map(comb => comb.values[field.index]))
    });

    return fields;
  }, [] as Array<IWidgetCascadeRequestField>);
}
